import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import {
  GroupType,
  OptionType,
} from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { OwnerModelFormDTO } from '../../../../OtherAssets.meta';

interface Props<T extends FieldValues> {
  index: number;
  control: Control<T>;
  ownerModelFields: OwnerModelFormDTO[];
  allowsUnequalOwnership?: boolean;
  groupOptions: GroupType<OptionType<string>>[];
  label: string;
  onUpdate: (data: { label: string; value: string | null }) => void;
  setCustomised?: () => void;
  errorMessage?: string;
  disabled?: boolean;
}

export function OwnershipSelect<T extends FieldValues>({
  index,
  control,
  ownerModelFields,
  allowsUnequalOwnership = false,
  groupOptions,
  label,
  onUpdate,
  setCustomised,
  errorMessage,
  disabled: disabledProp,
}: Props<T>) {
  const { options: peopleOptions } = groupOptions[0];

  /** Based on Asset Type,
   * If unequal ownership cannot be added, then "Joint" option can be shown
   * If unequal ownership can be added and "Customise" button is not clicked, then "Joint" option can be shown
   * If unequal ownership can be added and "Customise" button is clicked, then "Joint" option should be hidden
   */
  const selectedOwners = allowsUnequalOwnership
    ? !setCustomised
      ? ownerModelFields.map(owner => owner.Name + (owner.Id ?? 'null')).concat(['Jointnull'])
      : ownerModelFields.map(owner => owner.Name + (owner.Id ?? 'null'))
    : [];

  return (
    <Controller
      control={control}
      name={`ownerModel.${index}.Id` as Path<T>}
      render={({ field: { value, onChange, disabled } }) => {
        const isDisabled = disabled || disabledProp;

        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={errorMessage ? 'center' : 'end'}
            css={{
              gap: 8,
              flex: 1,
            }}
          >
            <Select
              css={{ flex: 1 }}
              isPortal
              disabled={isDisabled}
              value={groupOptions
                .map(({ options }) => options)
                .flat()
                .filter(option =>
                  option.value !== null
                    ? option.value === value
                    : option.label === ownerModelFields[index].Name,
                )}
              options={groupOptions.map(group => ({
                ...group,
                options: group.options.filter(
                  val => !selectedOwners.includes(val.label + (val.value ?? 'null')),
                ),
              }))}
              onChange={owner => {
                if (owner) {
                  onChange(owner?.value);
                  onUpdate(owner);
                }
              }}
              label={label}
              formVariant={errorMessage ? 'error' : undefined}
              helperText={errorMessage}
            />
            {allowsUnequalOwnership && setCustomised && !isDisabled && (
              <Button leftIcon="fas fa-cog" variant="secondary" onClick={setCustomised}>
                Customize
              </Button>
            )}
          </Box>
        );
      }}
    />
  );
}
